import {
  ControlledInputProps,
  ExtranetContainer,
  useBreakpoint,
  useDebounce,
} from '@pharmac-extranet/shared'
import { PharmacH6, PharmacSwitch, SpacerDefs } from '@pharmac/ui'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'

type ApprovalSwitchProps = {
  inverseValue?: boolean
  initialValue?: string | null
  label: string
  onChange?: (param: boolean) => void
  children?: ReactNode | undefined
  disabled?: boolean
} & SpacerDefs &
  ControlledInputProps

export const ApprovalSwitch = ({
  initialValue,
  label,
  onChange,
  children,
  disabled,
  inverseValue,
  ...props
}: ApprovalSwitchProps) => {
  const { minTablet, minDesktop, minMobile, maxMobile } = useBreakpoint()
  const [state, setState] = useState(!!initialValue)

  const [debounced, setDebounce] = useDebounce(!!initialValue)

  const initRef = useRef<boolean>(!!initialValue)

  useEffect(() => {
    setDebounce(state)
  }, [state])

  useEffect(() => {
    if (
      typeof initRef.current === 'boolean' &&
      debounced !== initRef.current &&
      typeof onChange === 'function'
    ) {
      onChange(debounced)

      initRef.current = debounced
    }
  }, [debounced])

  return (
    <ExtranetContainer
      flexRow={minTablet}
      flexColumn={minMobile}
      full
      {...props}
    >
      <ExtranetContainer
        full
        alignCenter
      >
        <ExtranetContainer
          flexColumn
          paddingMediumRight
          twoThirds={minTablet}
          full={!minDesktop}
        >
          <PharmacH6>{label}</PharmacH6>
          {minTablet && <>{children}</>}
        </ExtranetContainer>
        <ExtranetContainer
          paddingLargeLeft={minTablet}
          paddingSmallTop={maxMobile}
          flexColumn
          third={!minTablet}
        >
          {!props.control ? (
            <PharmacSwitch
              disabled={disabled}
              value={state}
              onChange={(value) => setState(value)}
            />
          ) : (
            <Controller
              rules={props.rules}
              name={props.name}
              control={props.control}
              render={({ field }) => (
                <PharmacSwitch
                  value={inverseValue ? !field.value : field.value}
                  onChange={(value) => {
                    field.onChange(inverseValue ? !value : value)
                  }}
                />
              )}
            />
          )}
        </ExtranetContainer>
      </ExtranetContainer>
      {!minTablet && <>{children}</>}
    </ExtranetContainer>
  )
}
